/**
 * The single HTTP client. Every request in the app goes through `api`.
 *
 * WHAT THIS FILE IS RESPONSIBLE FOR
 *
 *  1. COOKIES, NOT TOKENS. `agenterp_at` / `agenterp_rt` are HttpOnly — this code cannot
 *     read them and does not try. It sends `credentials: 'include'` and lets the browser
 *     do it. There is no token in localStorage, in a module variable, or in React state.
 *
 *  2. CSRF. `agenterp_csrf` IS readable by JS (that is the point of double-submit). Every
 *     mutating request echoes it in `x-csrf-token`. The three exempt endpoints
 *     (`/auth/login*`, `/auth/refresh`, `/auth/sso/*`) do not need it, and sending it
 *     anyway is harmless.
 *
 *  3. SILENT REFRESH. A 401 whose body says the access token expired is retried exactly
 *     once, after a single shared `POST /auth/refresh`. Concurrent 401s queue behind that
 *     one refresh rather than stampeding it — otherwise a dashboard with nine parallel
 *     queries rotates the refresh token nine times and the reuse detector revokes the
 *     session.
 *
 *  4. ERRORS THAT KEEP THEIR MEANING. The backend spends real effort on specific,
 *     actionable messages, including a 403 that names `requiredPermission`. `ApiError`
 *     carries all of it through to the UI verbatim. Nothing in this app ever renders the
 *     word "Forbidden".
 *
 *     The API emits TWO error envelopes and they are not interchangeable:
 *
 *       FLAT    `{ error: "…", code, requiredPermission }`  — permission denials
 *       NESTED  `{ error: { code, message, issues } }`      — auth, validation, 404,
 *                                                             rate limit, store, 500
 *
 *     `toApiError` used to read `body.error` and assume a string. For the nested half —
 *     which is every Zod failure in the product — that assigned an OBJECT to
 *     `Error.message`, so screens rendered "[object Object]", `code` was null, and
 *     `issues` was empty, meaning NO FORM EVER SHOWED A FIELD-LEVEL MESSAGE. Both
 *     envelopes now go through `normaliseApiError` in `@shared/api`, which is the one
 *     place either shape is described.
 */

import { normaliseApiError, type ApiFieldIssue } from '@shared/api';

export type { ApiFieldIssue };

export type ApiErrorKind =
  | 'NETWORK'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'VALIDATION'
  | 'RATE_LIMITED'
  | 'SERVER'
  | 'UNKNOWN';

export class ApiError extends Error {
  readonly status: number;
  readonly kind: ApiErrorKind;
  /** Machine code, e.g. `AUTHZ_PERMISSION_DENIED`, `VALIDATION_FAILED`, `LEAVE_NOT_FOUND`. */
  readonly code: string | null;
  /** On a 403 the permission the route demanded, e.g. `payroll.run.release`. */
  readonly requiredPermission: string | null;
  readonly requiredPermissionDescription: string | null;
  readonly requiredScope: string | null;
  readonly heldScope: string | null;
  readonly issues: readonly ApiFieldIssue[];
  readonly details: Record<string, unknown>;
  readonly url: string;

  constructor(init: {
    status: number;
    kind: ApiErrorKind;
    message: string;
    url: string;
    code?: string | null;
    requiredPermission?: string | null;
    requiredPermissionDescription?: string | null;
    requiredScope?: string | null;
    heldScope?: string | null;
    issues?: readonly ApiFieldIssue[];
    details?: Record<string, unknown>;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.kind = init.kind;
    this.url = init.url;
    this.code = init.code ?? null;
    this.requiredPermission = init.requiredPermission ?? null;
    this.requiredPermissionDescription = init.requiredPermissionDescription ?? null;
    this.requiredScope = init.requiredScope ?? null;
    this.heldScope = init.heldScope ?? null;
    this.issues = init.issues ?? [];
    this.details = init.details ?? {};
  }

  /** Field-keyed messages, for binding straight onto a form. */
  fieldErrors(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const issue of this.issues) {
      if (map[issue.field] === undefined) map[issue.field] = issue.message;
    }
    return map;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cookies
// ─────────────────────────────────────────────────────────────────────────────

const CSRF_COOKIE = 'agenterp_csrf';
const CSRF_HEADER = 'x-csrf-token';

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Session-expiry broadcast
// ─────────────────────────────────────────────────────────────────────────────

type SessionListener = (reason: 'EXPIRED' | 'SIGNED_OUT') => void;
const sessionListeners = new Set<SessionListener>();

/**
 * Called when the refresh attempt itself fails — the session is genuinely over.
 * `AuthProvider` subscribes and swaps the app to the sign-in screen. This is a callback
 * rather than a `window.location` assignment so a half-typed timesheet can be preserved
 * and the user returned to it after signing back in.
 */
export function onSessionLost(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

function announceSessionLost(reason: 'EXPIRED' | 'SIGNED_OUT'): void {
  for (const listener of sessionListeners) listener(reason);
}

// ─────────────────────────────────────────────────────────────────────────────
// Request
// ─────────────────────────────────────────────────────────────────────────────

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialised as JSON unless it is a `FormData`. */
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Set for the refresh call itself, so a failed refresh cannot recurse. */
  skipRefresh?: boolean;
  headers?: Record<string, string>;
}

const BASE = '/api';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = path.startsWith('/') ? `${BASE}${path}` : `${BASE}/${path}`;
  if (query === undefined) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const serialised = search.toString();
  return serialised === '' ? url : `${url}?${serialised}`;
}

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409 || status === 412) return 'CONFLICT';
  if (status === 400 || status === 422) return 'VALIDATION';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'SERVER';
  return 'UNKNOWN';
}

async function toApiError(response: Response, url: string): Promise<ApiError> {
  let body: unknown = {};
  try {
    const text = await response.text();
    if (text !== '') body = JSON.parse(text);
  } catch {
    // A non-JSON error body (nginx 502 page, for instance). Fall through to the default.
  }

  // ONE reader for both envelopes. See `@shared/api` for why there are two.
  const normalised = normaliseApiError(body);

  const message =
    normalised.message ??
    (response.status >= 500
      ? 'The server could not complete that request. It has been logged — if it keeps happening, contact your administrator with the time it occurred.'
      : `The request to ${url} failed with status ${response.status}.`);

  return new ApiError({
    status: response.status,
    kind: kindFor(response.status),
    message,
    url,
    code: normalised.code,
    requiredPermission: normalised.requiredPermission,
    requiredPermissionDescription: normalised.requiredPermissionDescription,
    requiredScope: normalised.requiredScope,
    heldScope: normalised.heldScope,
    issues: normalised.issues,
    details: normalised.details,
  });
}

/** Codes on a 401 that mean "the access token aged out", as opposed to "no session". */
const REFRESHABLE_CODES = new Set(['TOKEN_EXPIRED', 'AUTH_TOKEN_EXPIRED', 'ACCESS_TOKEN_EXPIRED']);

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all observe the result.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

async function execute<T>(path: string, options: RequestOptions, attempt: number): Promise<T> {
  const method = options.method ?? 'GET';
  const url = buildUrl(path, options.query);

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf !== null) headers[CSRF_HEADER] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers,
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      ...(options.body === undefined
        ? {}
        : { body: isForm ? (options.body as FormData) : JSON.stringify(options.body) }),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({
      status: 0,
      kind: 'NETWORK',
      url,
      message:
        'Could not reach the server. Check your connection — your work has not been saved. ' +
        'Retry once you are back online.',
    });
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text === '' ? undefined : (JSON.parse(text) as T)) as T;
  }

  const error = await toApiError(response, url);

  if (
    error.status === 401 &&
    attempt === 0 &&
    options.skipRefresh !== true &&
    !path.startsWith('/auth/')
  ) {
    if (error.code === null || REFRESHABLE_CODES.has(error.code) || error.code.includes('EXPIRED')) {
      const refreshed = await refreshSession();
      if (refreshed) return execute<T>(path, options, attempt + 1);
    }
    announceSessionLost('EXPIRED');
  } else if (error.status === 401 && !path.startsWith('/auth/')) {
    announceSessionLost('EXPIRED');
  }

  throw error;
}

export const api = {
  request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return execute<T>(path, options, 0);
  },
  get<T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal): Promise<T> {
    return execute<T>(
      path,
      { method: 'GET', ...(query === undefined ? {} : { query }), ...(signal === undefined ? {} : { signal }) },
      0,
    );
  },
  post<T>(path: string, body?: unknown, query?: RequestOptions['query']): Promise<T> {
    return execute<T>(path, { method: 'POST', body, ...(query === undefined ? {} : { query }) }, 0);
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return execute<T>(path, { method: 'PATCH', body }, 0);
  },
  put<T>(path: string, body?: unknown): Promise<T> {
    return execute<T>(path, { method: 'PUT', body }, 0);
  },
  delete<T>(path: string, body?: unknown): Promise<T> {
    return execute<T>(path, { method: 'DELETE', body }, 0);
  },
  signalSignedOut(): void {
    announceSessionLost('SIGNED_OUT');
  },
};
